import { useState } from 'react'
import { useWeb3 } from '../contexts/Web3Context'
import { SPONSORSHIP_AMOUNT_PLS } from '../constants'

interface SponsorPanelProps {
  botType: 'bridge' | 'plsx'
  botAddress: string
}

const DESCRIPTIONS: Record<SponsorPanelProps['botType'], string> = {
  bridge: 'Send PLS to sponsor new PulseChain bridgers',
  plsx: 'Send PLS to sponsor PLSX sacrificers',
}

const MULTIPLIERS: Array<{ label: string; value: bigint }> = [
  { label: 'Sponsor 1x', value: 1n },
  { label: '10x', value: 10n },
  { label: '100x', value: 100n },
  { label: '1,000x', value: 1000n },
  { label: '10,000x', value: 10000n },
]

const PLACEHOLDER_ADDRESS = '0x0000000000000000000000000000000000000000'

function truncateAddress(address: string): string {
  return `${address.slice(0, 8)}...${address.slice(-6)}`
}

export default function SponsorPanel({ botType, botAddress }: SponsorPanelProps) {
  const { address, isConnected, isWrongChain, openModal, switchToPulsechain, walletClient } = useWeb3()
  const [isLoading, setIsLoading] = useState(false)
  const [copied, setCopied] = useState(false)

  const loading = !botAddress
  const displayAddress = botAddress || PLACEHOLDER_ADDRESS

  const copyAddress = async () => {
    if (!botAddress) return
    await navigator.clipboard.writeText(botAddress)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleSponsor = async (multiplier: bigint) => {
    if (!isConnected) {
      openModal()
      return
    }
    if (isWrongChain) {
      switchToPulsechain()
      return
    }
    if (!walletClient || !address) return

    setIsLoading(true)
    try {
      const hash = await walletClient.sendTransaction({
        account: address,
        to: botAddress as `0x${string}`,
        value: multiplier * SPONSORSHIP_AMOUNT_PLS,
      })
      console.log('Sponsor transaction:', hash)
    } catch (error) {
      console.error('Error sponsoring:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-400">{DESCRIPTIONS[botType]}</p>

      <div className="flex items-center gap-2 bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2.5">
        <span className={`text-sm font-mono truncate flex-1 hidden sm:block ${loading ? 'bg-slate-700 rounded animate-pulse text-transparent' : 'text-slate-300'}`}>
          {displayAddress}
        </span>
        <span className={`text-sm font-mono sm:hidden ${loading ? 'bg-slate-700 rounded animate-pulse text-transparent' : 'text-slate-300'}`}>
          {truncateAddress(displayAddress)}
        </span>
        <button
          onClick={copyAddress}
          disabled={loading}
          className="text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0 disabled:opacity-30"
          title="Copy address"
        >
          {copied ? (
            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          )}
        </button>
      </div>

      <div className="flex gap-2">
        {MULTIPLIERS.map(({ label, value }, index) => (
          <button
            key={label}
            onClick={() => handleSponsor(value)}
            disabled={isLoading}
            className={
              index === 0
                ? 'flex-1 py-2.5 text-sm font-medium text-white bg-pulse-gradient hover:opacity-90 rounded-lg disabled:opacity-50 transition-colors'
                : 'py-2.5 px-3 text-sm font-medium text-slate-300 bg-slate-700/50 hover:bg-slate-600 border border-slate-600/50 rounded-lg disabled:opacity-50 transition-colors'
            }
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  )
}
